import React, { useMemo, useState } from "react";
import DateRangePicker from "../../finance/components/DateRangePicker";
import StaffPayDetail from "./StaffPayDetail";
import {
  buildBreakdown,
  computePay,
  formatHours,
  formatMoney,
  rateSuffix,
} from "./payCalc";
import "./StaffPay.css";
import { hairdresserService } from "../../../_services/hairdresser.service";

// ── Pay config persistence ──
// Pay basis/rate live on two backend fields on the staff record (payBasis,
// payRate): read from the staff list response, written via the owner-only
// /user/updateStaffPay endpoint. `config` below is an in-session optimistic
// cache so a just-saved value shows immediately, before the next staff fetch.

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function initials(name = "") {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}
function fmtRange(start, end) {
  const o = { day: "numeric", month: "short", year: "numeric" };
  return `${start.toLocaleDateString("en-GB", o)} – ${end.toLocaleDateString("en-GB", o)}`;
}

const PRESET_LABELS = {
  today: "Today",
  yesterday: "Yesterday",
  last7: "Last 7 days",
  last4weeks: "Last 4 weeks",
  last6months: "Last 6 months",
  last12months: "Last 12 months",
  wtd: "Week to date",
  mtd: "Month to date",
  qtd: "Quarter to date",
  ytd: "Year to date",
};

function StaffPay({ staffRota }) {
  const today = new Date();
  const [start, setStart] = useState(startOfMonth(today));
  const [end, setEnd] = useState(today);
  const [preset, setPreset] = useState("mtd");
  const [pickerOpen, setPickerOpen] = useState(false);

  // staffId -> { basis, rate }
  const [config, setConfig] = useState({});
  const [selectedId, setSelectedId] = useState(null);

  const currency =
    staffRota?.rota?.find((s) => s?.hairdresserInfo?.currencySymbol)?.hairdresserInfo
      ?.currencySymbol || "£";

  // Employed staff only (not self-employed, not the salon owner record).
  const employed = useMemo(
    () =>
      (staffRota?.rota || []).filter(
        (s) =>
          s?.name &&
          s.name !== "Unknown" &&
          s?.hairdresserInfo?.employmentTypeId == 1 &&
          s?.hairdresserInfo?.isSalonOwner != 1
      ),
    [staffRota]
  );

  // Pay config for a staff member: backend field if present, else local cache.
  const getConfig = (staff) => {
    const id = staff._id;
    const fromApi =
      staff?.hairdresserInfo?.payBasis || staff?.hairdresserInfo?.payRate != null
        ? {
            basis: staff.hairdresserInfo.payBasis || "Hourly",
            rate: staff.hairdresserInfo.payRate,
          }
        : null;
    return config[id] || fromApi || null;
  };

  const rows = useMemo(
    () =>
      employed.map((staff) => {
        const cfg = getConfig(staff);
        const days = buildBreakdown(staff.rotaInfo, start, end);
        const calc = computePay({
          payBasis: cfg?.basis || "Hourly",
          payRate: cfg?.rate || 0,
          days,
          rotaInfo: staff.rotaInfo,
        });
        return { staff, cfg, calc };
      }),
    // eslint-disable-next-line
    [employed, config, start, end]
  );

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, r) => {
        acc.pay += r.cfg ? r.calc.pay : 0;
        acc.hours += r.calc.totalHours;
        acc.unset += r.cfg ? 0 : 1;
        return acc;
      },
      { pay: 0, hours: 0, unset: 0 }
    );
  }, [rows]);

  const applyRange = (s, e, p) => {
    setStart(s);
    setEnd(e);
    setPreset(p || null);
  };

  const handleSave = (staff, next) => {
    // Optimistic: reflect the change immediately, then persist (owner-only).
    // Roll back on failure so the UI never shows a value that wasn't saved.
    const prev = config;
    const updated = { ...config, [staff._id]: { basis: next.basis, rate: next.rate } };
    setConfig(updated);
    hairdresserService
      .updateStaffPay({ userId: staff._id, payBasis: next.basis, payRate: next.rate })
      .catch(() => {
        setConfig(prev);
        alert("Couldn't save pay — please try again.");
      });
  };

  const selectedRow = rows.find((r) => r.staff._id === selectedId);

  return (
    <div className="spc">
      {/* Header: title + range picker */}
      <div className="spc-header">
        <div>
          <h2 className="spc-title">Staff Pay</h2>
          <p className="spc-sub">Pay for your employed team, based on hours worked.</p>
        </div>
        <div className="spc-range" style={{ position: "relative" }}>
          <button className="spc-range-btn" onClick={() => setPickerOpen((o) => !o)}>
            <span className="spc-range-label">Date range</span>
            <span className="spc-range-div" />
            <span className="spc-range-value">
              {preset && PRESET_LABELS[preset] ? PRESET_LABELS[preset] : fmtRange(start, end)}
            </span>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6" /></svg>
          </button>
          <DateRangePicker
            isOpen={pickerOpen}
            onClose={() => setPickerOpen(false)}
            startDate={start}
            endDate={end}
            selectedPreset={preset}
            onPresetChange={setPreset}
            onApply={applyRange}
            align="right"
          />
        </div>
      </div>

      {/* Summary */}
      <div className="spc-summary">
        <div className="spc-stat">
          <span className="spc-stat-label">Total pay</span>
          <span className="spc-stat-value">{formatMoney(totals.pay, currency)}</span>
        </div>
        <div className="spc-stat">
          <span className="spc-stat-label">Total hours</span>
          <span className="spc-stat-value">{formatHours(totals.hours)}</span>
        </div>
        <div className="spc-stat">
          <span className="spc-stat-label">Employed staff</span>
          <span className="spc-stat-value">{employed.length}</span>
        </div>
        {totals.unset > 0 && (
          <div className="spc-stat spc-stat--warn">
            <span className="spc-stat-label">Pay not set</span>
            <span className="spc-stat-value">{totals.unset}</span>
          </div>
        )}
      </div>

      {/* Staff rows */}
      {employed.length === 0 ? (
        <div className="spc-empty">
          <p>No employed staff yet. Add a team member with the "Employed" type to set up their pay.</p>
        </div>
      ) : (
        <div className="spc-list">
          {rows.map(({ staff, cfg, calc }) => (
            <button className="spc-card" key={staff._id} onClick={() => setSelectedId(staff._id)}>
              <div className="spc-avatar">{initials(staff.name)}</div>
              <div className="spc-who">
                <div className="spc-name">{staff.name}</div>
                <div className="spc-meta">
                  {formatHours(calc.totalHours)} · {calc.daysWorked} day{calc.daysWorked === 1 ? "" : "s"}
                  {calc.leaveDays > 0 && <> · {calc.leaveDays} leave</>}
                  {calc.overrunDays > 0 && <span className="spc-overrun-dot" title="Worked past shift">Overrun</span>}
                </div>
              </div>

              {cfg ? (
                <span className="spc-config">
                  <span className="spc-config-rate">{formatMoney(cfg.rate, currency)}</span>
                  <span className="spc-config-suffix">{rateSuffix(cfg.basis)}</span>
                </span>
              ) : (
                <span className="spc-set-pay">Set pay</span>
              )}

              <div className="spc-total">
                {!cfg ? (
                  <span className="spc-total-dash">—</span>
                ) : calc.needsRota ? (
                  <span className="spc-needs-rota">Needs rota</span>
                ) : (
                  <span className="spc-total-value">{formatMoney(calc.pay, currency)}</span>
                )}
              </div>

              <span className="spc-chev">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Detail / edit panel */}
      {selectedRow && (
        <StaffPayDetail
          staff={selectedRow.staff}
          config={selectedRow.cfg}
          currency={currency}
          start={start}
          end={end}
          rangeLabel={fmtRange(start, end)}
          onClose={() => setSelectedId(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}

export default StaffPay;
